/* ERP · contrato — o modelo da "Ontologia do negócio" (memória CFB) que o Staff usa para as fichas ligadas, o Início de
   cada frente e as telas novas. A entidade central é o Doador (Padrinho e Guardião são papéis dele).

   GET  /api/v1/admin/erp/graph                         -> ErpGraph (todas as entidades, já com dado pessoal mascarado)
   POST /api/v1/admin/erp/reveal  { id, reason }        -> RevealResult (CPF/e-mail/celular completos; fica na auditoria)
   GET  /api/v1/admin/erp/settings                      -> ErpSettings
   PUT  /api/v1/admin/erp/settings  ErpSettingsPatch    -> ErpSettings   (só admin)
   POST /api/v1/admin/erp/donors/crm     { ids }        -> CrmDonorsResult (dados do CRM, mascarados, dos doadores pedidos)
   POST /api/v1/admin/erp/donors/crm     { crmIds }     -> CrmDonorsResult com chave pelo GUID do CRM (ex.: linhas da inadimplência)
   POST /api/v1/admin/erp/donors/search  { q }          -> CrmSearchResult (busca por nome no CRM, 3+ letras)
   GET  /api/v1/admin/erp/donors/recent                 -> CrmRecentResult (os 20 doadores que chegaram por último no CRM)
   POST /api/v1/admin/erp/donors/link    LinkDonorInput -> LinkDonorResult (ensure no Hub + a ligação; 409 = conflito de identidade)
   POST /api/v1/admin/erp/accounts/crm   { crmIds }     -> CrmAccountsResult (nome das contas/empresas do CRM pedidas, pelo GUID)
   GET  /api/v1/admin/erp/crm/portfolio                 -> CrmPortfolioResult (carteira do CRM: ativos, retenção, entradas × saídas, canais)
   GET  /api/v1/admin/erp/crm/overdue?page=&limit=&sort=&minMonths= -> CrmOverdueResult (inadimplência por doador, sem nome)
   GET  /api/v1/admin/erp/crm/cases?status=&kind=&subject=&overdue=&osp=&page=&limit= -> CrmCasesResult (ocorrências, sem texto livre)
   GET  /api/v1/admin/erp/crm/children?list=&osp=&page=&limit= -> CrmChildrenResult (crianças por OSP e dias sem padrinho)
   GET  /api/v1/admin/erp/crm/campaigns                 -> CrmCampaignsResult (linhas por campanha de origem: ativas, encerradas)

   Decisão 2026-10-24: a base guarda chaves e LIGAÇÕES; nome e contato do doador vêm do CRM pelo Hub, sob demanda e
   mascarados. O nome que o grafo traz pode faltar (`nameInBase` false): a tela pede ao CRM só os da página visível.

   dataMode 'example' = dados fictícios do protótipo (sem base); 'live' = base de relacionamento pelo Hub. `coverage`
   diz o que a base já traz: o que for false a tela mostra como "ainda não sincronizado" (nunca inventa no live).
   Datas sempre AAAA-MM-DD; meses AAAA-MM. Ids: strings opacas (no exemplo: d1, i1, e1, co1, em1, a1, o1, k1). */

export const ENTITY_TYPES = ['doador', 'igreja', 'evento', 'consultor', 'empresa', 'embaixador', 'osp', 'crianca'] as const
export type EntityType = (typeof ENTITY_TYPES)[number]
export type EntityRef = { type: EntityType; id: string }

export const DONOR_STATUSES = ['lead', 'pre', 'ativo', 'inadimplente', 'cancelado'] as const
export type DonorStatus = (typeof DONOR_STATUSES)[number]
export const CHANNELS = ['live', 'igreja', 'empresa', 'embaixador', 'site', 'tele'] as const
export type Channel = (typeof CHANNELS)[number]
export type Role = 'padrinho' | 'guardiao'

export type ContractLine = {
    id: string
    product: 'Apadrinhamento' | 'Guardião da Infância'
    child: string | null
    value: number
    convertedAt: string
    status: 'ativa' | 'cancelada' | 'substituída'
    endedAt?: string | null
    osp?: string | null
}
export type Invoice = { month: string; due: string; value: number; method: string; status: 'paga' | 'negada → reprocessada' | 'em atraso' | 'aberta'; reason?: string | null }
export type CaseRecord = {
    id: string
    kind: 'Cancelamento' | 'Retenção' | 'Pedido especial' | 'Contato por atraso' | 'Visita presencial' | 'Repasse de DFC'
    opened: string
    status: 'aberta' | 'aguardando OSP' | 'respondida' | 'encerrada'
    child?: string | null
    osp?: string | null
    due?: string | null
    note: string
}
export type Letter = {
    id: string
    kind: 'Boas-vindas' | 'Amistosa' | 'Carta de DFC' | 'Resposta ao padrinho' | 'Relatório de progresso'
    child: string
    osp: string
    created: string
    due: string
    status: 'recebida' | 'no prazo' | 'vencendo' | 'atrasada'
}
export type Gift = { value: number; date: string; child: string; status: 'repasse pendente' | 'repassado à OSP' | 'pago à família' }
export type Visit = { date: string; child: string; kind: 'presencial' | 'online'; status: 'pedida' | 'aprovada' | 'realizada' }

export type Donor = {
    type: 'doador'
    id: string
    /** Nome da base; sem nome na base (linha criada pelo ensure), um rótulo neutro e `nameInBase` false. */
    name: string
    /** false = o nome real só no CRM (pedir por /erp/donors/crm). Ausente = veio da base (ou do exemplo). */
    nameInBase?: boolean
    /** contactid do CRM, quando a base já ligou (só live). */
    crmId?: string | null
    code: string
    contract: string | null
    city: string | null
    uf: string | null
    status: DonorStatus
    roles: Role[]
    channel: Channel
    /** Quem originou (igreja, empresa ou embaixador). */
    origin: EntityRef | null
    links: { igreja?: string; evento?: string; empresa?: string; embaixador?: string; consultor?: string }
    ficha: { evento: string; date: string; status: 'convertida' | 'incompleta → fechada no boleto' | 'completada pelo WhatsApp' } | null
    /** Data de conversão (1ª contribuição); no pré-ativo, a data do cadastro. */
    since: string
    payment: string | null
    monthly: number
    /** Já mascarados. Completo só por POST /erp/reveal. */
    cpf: string | null
    email: string | null
    phone: string | null
    canceledAt?: string | null
    cancelReason?: string | null
    lines: ContractLine[]
    invoices: Invoice[]
    cases: CaseRecord[]
    letters: Letter[]
    gifts: Gift[]
    visits: Visit[]
}
export type Church = {
    type: 'igreja'; id: string; name: string; city: string | null; uf: string | null
    kind: 'histórica' | 'pentecostal' | 'neopentecostal' | 'católica' | null
    denom: string | null; pastor: string | null; since: string | null
    /** Dias desde a última visita (null = nunca registrada). */
    lastVisit: number | null
    counterpart: string | null; eventContract: 'assinado' | 'no jurídico' | null; osp: string | null
}
export type EventRecord = {
    type: 'evento'; id: string; name: string; kind: 'Live Concert' | 'Ação corporativa' | 'Live online' | string
    date: string | null; upcoming: boolean
    hostType: 'igreja' | 'empresa' | null; hostId: string | null
    consultores: string[]; amb: string | null
    audience: number | null; fichas: number | null; reserved: string[]; cost: number | null
    /** GUID da campanha do CRM (minúsculo) do evento; liga as linhas de contrato ao evento. Só live; ausente = não veio. */
    crmCampaign?: string | null
}
export type Consultant = { type: 'consultor'; id: string; name: string; region: string | null; cnpj: string | null; since: string | null }
export type Company = { type: 'empresa'; id: string; name: string; place: string | null; program: string | null; osp: string | null; staff: number | null }
export type Ambassador = { type: 'embaixador'; id: string; name: string; cat: string | null; reach: string | null; since: string | null }
export type Osp = {
    type: 'osp'; id: string; name: string; place: string | null; uf: string | null; focus: string | null
    enrolled: number | null; educators: number | null
    subsidy: { month: string; status: 'aprovado pelo SMT' | 'pré-aprovado' | 'retido'; value: number } | null
    accountability: { month: string; status: 'aprovada' | 'em conferência' | 'reaberta para correção' } | null
}
/** Criança: só primeiro nome + inicial (política de proteção). */
export type Child = {
    type: 'crianca'; id: string; name: string; age: number | null; osp: string
    status: 'apadrinhada' | 'disponível' | 'reservada' | 'desativada'
    enrolledAt: string | null; sponsor: string | null; daysWithout: number | null; reservedFor: string | null
}
export type Entity = Donor | Church | EventRecord | Consultant | Company | Ambassador | Osp | Child
export type EntityOf<T extends EntityType> = Extract<Entity, { type: T }>

export type Coverage = {
    contracts: boolean; invoices: boolean; cases: boolean; letters: boolean; gifts: boolean; visits: boolean
    children: boolean; consultants: boolean; fichas: boolean
}

export type ErpGraph = {
    dataMode: 'example' | 'live'
    today: string
    coverage: Coverage
    doador: Donor[]; igreja: Church[]; evento: EventRecord[]; consultor: Consultant[]
    empresa: Company[]; embaixador: Ambassador[]; osp: Osp[]; crianca: Child[]
}

export type RevealResult = { id: string; cpf: string | null; email: string | null; phone: string | null; message: string }

// --- Doador no CRM (lido pelo Hub, sob demanda, só mascarado) ---
/** ok = leu; off = sem CRM neste ambiente (exemplo); forbidden = a chave do Panel ainda não pode ler o CRM;
    unavailable = Hub/CRM fora agora. */
export type CrmState = 'ok' | 'off' | 'forbidden' | 'unavailable'
export type CrmDonor = {
    crmId: string
    name: string | null
    firstName: string | null
    city: string | null
    uf: string | null
    status: 'active' | 'inactive' | null
    statusReason: string | null
    createdAt: string | null
    modifiedAt: string | null
    activeContractLines: number | null
    openInvoices: number | null
    /** Já mascarados pelo Hub; o completo nunca sai do Hub. */
    email: string | null
    mobile: string | null
    phone: string | null
    cpf: string | null
}
/** `items` por id do doador na base ({ ids }) ou pelo GUID do CRM, minúsculo ({ crmIds }); quem não está no CRM fica de fora. */
export type CrmDonorsResult = { state: CrmState; items: Record<string, CrmDonor> }
/** Conta (empresa) no CRM, lida pelo Hub sob demanda: só nome, nome fantasia, cidade/UF, situação e data da última
    alteração (CNPJ, contatos e endereço nunca saem do Hub). */
export type CrmAccount = {
    crmId: string
    name: string | null
    tradeName: string | null
    city: string | null
    uf: string | null
    status: 'active' | 'inactive' | null
    modifiedAt: string | null
}
/** `items` pelo GUID da conta, minúsculo; quem não está no CRM fica de fora. */
export type CrmAccountsResult = { state: CrmState; items: Record<string, CrmAccount> }
/** `sponsorId` = já tem linha na base (abre a ficha); null = só no CRM (pode ligar). */
export type CrmSearchResult = { state: CrmState; items: (CrmDonor & { sponsorId: string | null })[] }
/** Mesmo formato da busca, do mais novo para o mais antigo no CRM. */
export type CrmRecentResult = CrmSearchResult
export const LINK_TARGETS = ['igreja', 'embaixador', 'empresa', 'evento'] as const
export type LinkTarget = (typeof LINK_TARGETS)[number]
/** Um dos dois: `sponsorId` (doador da base) ou `crmId` (resultado da busca no CRM). */
export type LinkDonorInput = { sponsorId?: string; crmId?: string; target: LinkTarget; targetId: string }
export type LinkDonorResult = { sponsorId: string; created: boolean; message: string }
export const IDENTITY_CONFLICT_MESSAGE = 'Conflito de identidade — a Tecnologia resolve em Configurações.'

// --- Carteira e inadimplência no CRM (exportações do Hub, agregadas no servidor do Panel) ---
/* O servidor lê do CRM pelo Hub (/v1/panel/exports: linhas de contrato de Apadrinhamento e faturas, sem dado pessoal),
   guarda em memória e atualiza a cada 30 min. As respostas trazem só GUID do doador e números: o nome vem do lote
   mascarado (useCrmDonors / POST /erp/donors/crm) só para as linhas visíveis.
   state: ok = dados da última leitura; stale = a última atualização falhou e vale a leitura de `updatedAt`;
   loading = a primeira leitura ainda não terminou; unavailable = Hub/CRM fora e nada lido ainda; forbidden = a chave do
   Panel não pode ler as exportações; off = sem CRM neste ambiente (exemplo). Sem dados (loading/unavailable/forbidden/off)
   os blocos vêm null: a tela mostra o estado, nunca número inventado. */
export type CrmFactsState = 'ok' | 'stale' | 'loading' | 'unavailable' | 'forbidden' | 'off'

/** Canal de origem do doador: a campanha de origem da PRIMEIRA linha dele (unidade de gestão Live!Concerts ou nome). */
export const CRM_CHANNELS = ['live', 'site', 'tele', 'empresa', 'outro', 'sem_campanha'] as const
export type CrmChannel = (typeof CRM_CHANNELS)[number]
export const CRM_CHANNEL_LABELS: Record<CrmChannel, string> = {
    live: 'Live!Concerts e igrejas',
    site: 'Site e digital',
    tele: 'Telemarketing',
    empresa: 'Corporativo',
    outro: 'Outras campanhas',
    sem_campanha: 'Sem campanha de origem',
}

/** Meses de casa em que a retenção é medida. */
export const RETENTION_HORIZONS = [2, 7, 12, 24] as const
/** survival: chance de seguir ativo (0 a 1, Kaplan-Meier); null = ninguém em observação até esse mês. */
export type SurvivalPoint = { months: number; survival: number | null; atRisk: number }
export type RetentionCurve = {
    /** Só relações iniciadas a partir desta data (AAAA-MM-DD): antes dela o CRM só tem quem estava ativo na migração. */
    cohortFrom: string
    customers: number
    exits: number
    /** Ainda ativos (contam até o tempo de casa que já têm). */
    censored: number
    points: SurvivalPoint[]
}
/** Mês AAAA-MM: doadores que começaram (1º apadrinhamento) e que saíram (última linha encerrada, nenhuma ativa). */
export type CrmMonthFlow = { month: string; entries: number; exits: number; net: number }
/** Linhas encerradas no mês. `within90Days` = encerradas até 90 dias do início; `replaced` = trocadas por outra linha
    (troca de criança, migração) e fora de `lines`; byReason = rótulo da razão do status no CRM. */
export type CrmMonthCancellations = { month: string; lines: number; within90Days: number; replaced: number; byReason: { label: string; count: number }[] }
export type CrmChannelRetention = { channel: CrmChannel; customers: number; active: number; retention: RetentionCurve }
export type CrmPortfolio = {
    today: string
    active: { lines: number; customers: number; monthlyAmount: number }
    retention: RetentionCurve
    /** Últimos 12 meses, do mais antigo ao atual (a tela de 6 meses usa os 6 últimos). */
    flow: CrmMonthFlow[]
    cancellations: CrmMonthCancellations[]
    channels: CrmChannelRetention[]
    /** O que ficou fora das contas por falta de dado no CRM (nada é preenchido). */
    excluded: { linesWithoutCustomer: number; customersWithoutStart: number; exitsWithoutDate: number }
}
export type CrmPortfolioResult = { state: CrmFactsState; updatedAt: string | null; portfolio: CrmPortfolio | null }

export type CrmOverdueCustomer = {
    /** GUID do doador no CRM (contactid ou accountid); o nome vem do lote mascarado. */
    customerId: string
    customerKind: 'contact' | 'account' | null
    invoices: number
    /** Meses distintos com fatura vencida em aberto. */
    overdueMonths: number
    amount: number
    oldestDueDate: string
    daysOverdue: number
    /** Forma de pagamento da fatura vencida mais recente. */
    method: string | null
    /** Último retorno do cartão na fatura vencida mais recente que tem um (débito não tem motivo no CRM). */
    denialReason: string | null
    pendingCancellation: boolean
    activeLines: number
}
export type CrmInvoiceTotals = { count: number; amount: number }
/** Faturas com vencimento no mês (AAAA-MM), pela situação de agora. */
export type CrmMonthInvoices = { month: string; due: CrmInvoiceTotals; paid: CrmInvoiceTotals; open: CrmInvoiceTotals; overdue: CrmInvoiceTotals; cancelled: CrmInvoiceTotals; other: CrmInvoiceTotals }
export type CrmOverdueSummary = {
    today: string
    customers: number
    invoices: number
    amount: number
    /** Doadores por meses em atraso. */
    byMonths: { one: number; two: number; threeOrMore: number }
    byMethod: { method: string | null; customers: number; amount: number }[]
    pendingCancellationInvoices: number
    month: CrmMonthInvoices
    /** Faturas que o Panel viu vencidas e em aberto e depois pagas. `since` = desde quando o Panel observa
        (o CRM não guarda a falha anterior); null = ainda sem leitura. */
    recovered: { since: string | null; month: string; invoices: number; amount: number }
}
export const CRM_OVERDUE_SORTS = ['oldest', 'amount'] as const
export type CrmOverdueSort = (typeof CRM_OVERDUE_SORTS)[number]
export type CrmOverdueResult = {
    state: CrmFactsState
    updatedAt: string | null
    summary: CrmOverdueSummary | null
    items: CrmOverdueCustomer[]
    total: number
    page: number
    pages: number
}

// --- Ocorrências do CRM (GET /admin/erp/crm/cases) ---
/* Sem texto livre (título, descrição, cartas: podem ter dado pessoal). Pessoas só pelo GUID: o nome do doador vem do
   lote mascarado (useCrmDonorsByCrmId); a criança aparece pelo código e, quando o CRM tem, o primeiro nome. */

export const CRM_CASE_KINDS = ['special-request', 'sponsorship-cancellation', 'regular-donation-cancellation', 'other'] as const
export type CrmCaseKind = (typeof CRM_CASE_KINDS)[number]
export const CRM_CASE_KIND_LABELS: Record<CrmCaseKind, string> = {
    'special-request': 'Pedido especial',
    'sponsorship-cancellation': 'Cancelamento de apadrinhamento',
    'regular-donation-cancellation': 'Cancelamento de doação regular',
    other: 'Outros assuntos',
}
/** De onde veio o prazo: SLA da ChildFund (new_sla_prazofinal), prazo do CRM (resolveby) ou a regra do pedido especial
    (abertura + Configurações › Regras). Ocorrência sem nenhum dos três não tem prazo (não fica vencida). */
export type CrmCaseDueSource = 'crm-sla' | 'crm-resolve-by' | 'rule'
export type CrmCaseItem = {
    id: string
    /** Número da ocorrência no CRM (ticketnumber). */
    number: string | null
    kind: CrmCaseKind
    subjectId: string | null
    subject: string | null
    status: 'open' | 'resolved' | 'cancelled' | null
    statusReason: string | null
    /** Canal de abertura (caseorigincode). */
    origin: string | null
    /** GUID do doador no CRM; o nome vem do lote mascarado. */
    customerId: string | null
    customerKind: 'contact' | 'account' | null
    contractLineId: string | null
    childId: string | null
    /** Código da criança (das crianças do CRM no Panel); null = não achada. */
    childCode: string | null
    childFirstName: string | null
    ospId: string | null
    /** Nome da OSP (organização), pelas crianças do CRM; null = não achado. */
    ospName: string | null
    /** AAAA-MM-DD (Brasília). */
    openedOn: string | null
    dueDate: string | null
    dueSource: CrmCaseDueSource | null
    /** Aberta: dias desde a abertura; fechada: null. */
    daysOpen: number | null
    overdue: boolean
    daysOverdue: number | null
    closedOn: string | null
}
export type CrmCaseSubjectCount = { subjectId: string | null; subject: string | null; kind: CrmCaseKind; open: number; overdue: number }
/** Pedidos de cancelamento de apadrinhamento abertos no mês e o que aconteceu com a linha de contrato:
    retained = ocorrência fechada e a linha segue ativa; cancelled = linha encerrada; unknown = sem linha ou linha não achada. */
export type CrmCancellationMonth = { month: string; requests: number; open: number; retained: number; cancelled: number; unknown: number; regularDonationRequests: number }
export type CrmOspCases = { ospId: string; ospName: string | null; open: number; overdue: number; total: number }
export type CrmCasesSummary = {
    today: string
    /** Guardadas: em aberto (qualquer data) e as abertas desde este dia. */
    windowFrom: string
    open: number
    openOverdue: number
    bySubject: CrmCaseSubjectCount[]
    specialRequests: {
        /** false = nenhum assunto com "pedido especial" no nome entre as ocorrências lidas (os números ficam 0). */
        subjectFound: boolean
        ruleDays: number
        open: number
        overdue: number
        /** Em aberto e vencendo nos próximos 3 dias. */
        dueSoon: number
    }
    /** Últimos 12 meses, do mais antigo ao atual. */
    cancellations: CrmCancellationMonth[]
    byOsp: CrmOspCases[]
    excluded: { withoutOpenDate: number }
}
export const CRM_CASE_STATUS_FILTERS = ['open', 'closed', 'all'] as const
export type CrmCasesResult = {
    state: CrmFactsState
    updatedAt: string | null
    summary: CrmCasesSummary | null
    items: CrmCaseItem[]
    total: number
    page: number
    pages: number
}

// --- Crianças do CRM (GET /admin/erp/crm/children) ---
/* Menores de idade: só GUID, código, primeiro nome, situação, OSP e as datas da regra dos dias sem padrinho.
   "Sem padrinho desde" = o maior entre o cadastro no CRM e o último encerramento de apadrinhamento (o CRM não tem a
   data em que a criança ficou disponível). Desativada: o CRM não guarda a data; o Panel marca o dia em que viu a
   criança passar a desativada (`deactivated.since` = desde quando observa). */

export const CRM_CHILD_STATUSES = ['sponsored', 'available', 'reserved', 'unavailable', 'inactive', 'other'] as const
export type CrmChildStatus = (typeof CRM_CHILD_STATUSES)[number]
export const CRM_CHILD_STATUS_LABELS: Record<CrmChildStatus, string> = {
    sponsored: 'Apadrinhada',
    available: 'Disponível',
    reserved: 'Reservada',
    unavailable: 'Indisponível',
    inactive: 'Desativada',
    other: 'Outra situação',
}
export type CrmChildItem = {
    id: string
    code: string | null
    firstName: string | null
    status: CrmChildStatus | null
    ospId: string | null
    ospName: string | null
    /** AAAA-MM-DD; null = sem data no CRM (fica fora da conta de dias). */
    waitingSince: string | null
    waitingSinceSource: 'last-sponsorship' | 'registered' | null
    /** Só para ativa sem padrinho (disponível, reservada, indisponível, outra). */
    daysWithoutSponsor: number | null
    /** Dia em que o Panel viu a criança passar a desativada. */
    deactivatedOn: string | null
}
export type CrmChildCounts = {
    total: number
    sponsored: number
    available: number
    reserved: number
    unavailable: number
    inactive: number
    other: number
    /** Situação que o CRM não informou. */
    unknown: number
    /** Ativas sem padrinho (disponível, reservada, indisponível, outra). */
    withoutSponsor: number
    /** Disponível ou reservada com dias sem padrinho entre o aviso e o prazo. */
    nearLimit: number
    /** Disponível ou reservada com dias sem padrinho no prazo ou além. */
    overLimit: number
    /** Desativadas que o Panel viu nos últimos 30 dias. */
    deactivatedRecently: number
}
export type CrmOspChildren = CrmChildCounts & { ospId: string | null; ospName: string | null }
export type CrmChildrenSummary = {
    today: string
    alertDays: number
    deactivateDays: number
    totals: CrmChildCounts
    byOsp: CrmOspChildren[]
    /** Desde quando o Panel observa as desativações (o CRM não guarda a data). */
    deactivated: { since: string | null; days: number }
    excluded: { withoutDate: number; withoutOsp: number }
}
export const CRM_CHILD_LISTS = ['near', 'over', 'waiting', 'deactivated'] as const
export type CrmChildList = (typeof CRM_CHILD_LISTS)[number]
export type CrmChildrenResult = {
    state: CrmFactsState
    updatedAt: string | null
    summary: CrmChildrenSummary | null
    items: CrmChildItem[]
    total: number
    page: number
    pages: number
}

// --- Linhas por campanha do CRM (GET /admin/erp/crm/campaigns) ---
/* Linhas de contrato de Apadrinhamento agrupadas pela campanha de origem (só números; sem doador). A tela liga a
   campanha ao evento da base (EventRecord.crmCampaign) para ver padrinhos e saídas de cada Live Concert e igreja.
   Linha trocada por outra (troca de criança, migração: alguma linha aponta para ela como origem) fica fora de tudo
   e conta em `replaced`. `endedRecent` = encerradas nos últimos `recentDays` dias. */
export const CRM_CAMPAIGN_RECENT_DAYS = 90
export type CrmCampaignStat = {
    campaignId: string
    campaignName: string | null
    unitCode: number | null
    lines: number
    activeLines: number
    /** Encerradas (statecode inativo), com ou sem data. */
    endedLines: number
    endedRecent: number
    customers: number
    activeCustomers: number
    /** AAAA-MM-DD da 1ª e da última linha (início). */
    firstStart: string | null
    lastStart: string | null
    replaced: number
}
export type CrmCampaignsResult = {
    state: CrmFactsState
    updatedAt: string | null
    today: string | null
    recentDays: number
    items: CrmCampaignStat[]
}

// --- Configurações do ERP (Configurações › Frentes e telas, Permissões, Regras, Organização) ---

export const FRENTE_KEYS = ['relacionamento', 'captacao', 'corporativo', 'marketing', 'programas', 'financeiro', 'gestao', 'tecnologia'] as const
export type FrenteKey = (typeof FRENTE_KEYS)[number]

export type ErpRules = {
    letterDays: { boasVindas: number; amistosa: number; dfc: number; resposta: number }
    specialRequestDays: number
    childDeactivateDays: number
    childAlertDays: number
    churchRevisitDays: number
    liveConversionTarget: number
    fieldTarget: number
    split: { osp: number; cfb: number }
    overdueAlert: { on: boolean; time: string }
}
export type ErpSettings = {
    /** Por frente: telas do menu (chaves de ERP_VIEWS, na ordem) e se vê dado pessoal. Ausente = padrão de frentes.ts. */
    frentes: Partial<Record<FrenteKey, { nav?: string[]; pii?: boolean }>>
    rules: ErpRules
    org: { name: string; city: string; timezone: string; ssoMicrosoft: boolean; require2fa: boolean; sessionHours: number }
    updatedAt: string | null
    updatedBy: string | null
}
export type ErpSettingsPatch = Partial<Pick<ErpSettings, 'frentes' | 'org'>> & { rules?: Partial<ErpRules> }

// --- Usuários do panel (Configurações › Usuários e acessos) ---
/* GET  /api/v1/admin/users            -> PanelUser[]  (só admin)
   POST /api/v1/admin/users  UserInput -> PanelUser    (só admin; cria ou atualiza pelo e-mail)
   Quando a tabela tem usuários, ela é a fonte do perfil de cada pessoa (quem não está nela entra como "Somente leitura")
   e os ativos podem entrar no panel. Sempre fica pelo menos um admin ativo. */
export type PanelUser = {
    email: string
    name: string
    role: import('../admin/shared/permissions').AdminRole
    title: string | null
    department: string | null
    active: boolean
    updatedAt: string
    updatedBy: string
    /** Quando a pessoa criou a senha (null = ainda não criou: precisa de um link de acesso). */
    passwordSetAt: string | null
    /** Validade do link de acesso pendente (null = nenhum). */
    accessLinkExpiresAt: string | null
}
/** POST /api/v1/admin/users/access-link { email } -> AccessLink (só admin): link de uso único para criar/trocar a senha. */
export type AccessLink = { url: string; expiresAt: string }
export type UserInput = Pick<PanelUser, 'email' | 'name' | 'role' | 'title' | 'department' | 'active'>
